import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  TextField,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { listUsers, updateUser } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

interface AdminUser {
  id: number;
  email: string;
  full_name: string | null;
  is_admin: boolean;
  is_active: boolean;
  token_limit: number;
  tokens_used: number;
  created_at: string | null;
  last_login: string | null;
}

export default function Admin() {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editLimits, setEditLimits] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState<number | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    try {
      const { users } = await listUsers();
      setUsers(users as AdminUser[]);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }

  async function handleToggle(userId: number, field: "is_active" | "is_admin", value: boolean) {
    setSaving(userId);
    try {
      await updateUser(userId, { [field]: value });
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, [field]: value } : u))
      );
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(null);
    }
  }

  async function handleSaveLimit(userId: number) {
    const val = parseInt(editLimits[userId], 10);
    if (isNaN(val) || val < 0) return;
    setSaving(userId);
    try {
      await updateUser(userId, { token_limit: val });
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, token_limit: val } : u))
      );
      setEditLimits((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(null);
    }
  }

  async function handleResetTokens(userId: number) {
    setSaving(userId);
    try {
      await updateUser(userId, { tokens_used: 0 });
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, tokens_used: 0 } : u))
      );
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(null);
    }
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Admin access required.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        User Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="center">Admin</TableCell>
              <TableCell align="right">Credit Limit</TableCell>
              <TableCell align="right">Used</TableCell>
              <TableCell align="right">Remaining</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => {
              const remaining = u.token_limit - u.tokens_used;
              return (
                <TableRow key={u.id} sx={{ opacity: u.is_active ? 1 : 0.5 }}>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.full_name || "—"}</TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={u.is_active}
                      disabled={saving === u.id}
                      onChange={(_, checked) => handleToggle(u.id, "is_active", checked)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={u.is_admin}
                      disabled={saving === u.id}
                      onChange={(_, checked) => handleToggle(u.id, "is_admin", checked)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={editLimits[u.id] ?? u.token_limit}
                      onChange={(e) =>
                        setEditLimits((prev) => ({ ...prev, [u.id]: e.target.value }))
                      }
                      sx={{ width: 90 }}
                      slotProps={{ htmlInput: { min: 0, style: { textAlign: "right" } } }}
                    />
                    {editLimits[u.id] !== undefined && (
                      <IconButton
                        size="small"
                        onClick={() => handleSaveLimit(u.id)}
                        disabled={saving === u.id}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell align="right">{u.tokens_used}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={remaining}
                      size="small"
                      color={remaining < 10 ? "error" : remaining < 25 ? "warning" : "success"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {u.last_login
                      ? new Date(u.last_login).toLocaleDateString()
                      : "Never"}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      title="Reset credits used to 0"
                      onClick={() => handleResetTokens(u.id)}
                      disabled={saving === u.id || u.tokens_used === 0}
                    >
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
